import { Injectable } from "@angular/core";
import { LoggerService } from "@bfx/logger";
import { TranslateService } from "@ngx-translate/core";
import { Subject } from "rxjs";
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { DashboardItemType } from "../../../bfx-common/models/dashboard-item-type.enum";
import { GoldenLayoutAdditionMode } from "../../../golden-layout/golden-layout-addition-mode";
import {
  GLPopoutConfiguration,
  GLPopoutState,
  GLPopoutUndockBehaviour,
  GoldenLayoutService,
} from "../../../golden-layout/golden-layout-service";
const logger = LoggerService.for(__dirname);

import {
  ActiveUser,
  ActiveUserProfilePromiseService,
  CcyLookupService,
  CcyPairLookupService,
  InterventionDataLookupService,
  InterventionDataPromiseLookupService,
  NotificationService,
  ProfilePromiseActionService,
  ProfileSettingKeys,
  StaticDataPrivatizationType,
  UserPromiseLookupService,
} from "shared/cubiq-services";
// Models

import {
  FwdRollPricingCmd,
  FwdRollPricingCmdPayload,
} from "app/bfx-blotter-intervention/fwd-roll-pricing/models/fwd-roll-pricing-cmd-payload.model";
import { FwdRollPricingModel } from "app/bfx-blotter-intervention/fwd-roll-pricing/models/fwd-roll-pricing.model";
import { PortfolioPricingReference } from "app/bfx-blotter-intervention/models/bfx-intervention-contextmenu.models";
import { BfxSaveProfileNotificationService } from "app/bfx-common/services/bfx-save-profile-notification.service";
import { BfxUserProfileService } from "app/bfx-common/services/bfx-user-profile.service";
import { BfxDialogCommand } from "app/bfx-dialogs/models/bfx-dialog-command.enum";
import { BfxDialogData } from "app/bfx-dialogs/models/bfx-dialog-data.model";
import { FwdRollPriceActionService } from "shared/cubiq-services/action/impl/FwdRollPriceActionService";
import { allowedNodeEnvironmentFlags } from "process";
import { SysFeatures, SystemConfigPrimaryService } from "shared/auth-services";

// type PortfolioPricingModel =  any;
const textTemplates: {
  title_key: string;
  warning_key: string;
} = {
  title_key: "dealer.intervention.fwdroll.price.title",
  warning_key: "dealer.intervention.fwdroll.price.warning",
};

const CONFIRM_DIALOG_ID = "frps-confirm";

/**
 * This services acts a mediator/controller for all Fwd roll UI views - the intervention-action.service calls it
 * directly to present a Fwd Pricing view.
 * Each view is represented by an independent FwdRollPricingModel however only a single open winodw is supported at hr moment.
 * If there is already an open view when an open request arrives then the user is consulted and the existing
 * Fwd Pricing unlocked before locking the requested portfolio.
 * Unlock requests are fire-and-forget - if any issue occurs with the unlock, we can unlcok from the DI window.
 */
@Injectable()
export class FwdRollPricingService {
  /**********************************************************************/
  /* OBSERVABLE */
  // Observable string sources
  private blotterActionDialogSource = new Subject<BfxDialogData>();
  // Observable string streams
  public dialogCtrl$ = this.blotterActionDialogSource.asObservable();

  private activePortfolio: FwdRollPricingModel;

  private activeUser: ActiveUser;

  private activeWindowId = null;

  private hasView: boolean;
  savedSettings: any;

  /** When privatising bulk orders, should privateComments be overridden? */
  private privatizePrivateComments: boolean = false;
  /** When privatising bulk orders, should clientComments be overridden? */
  private privatizeClientComments: boolean = false;

  constructor(
    private translate: TranslateService,
    private layoutService: GoldenLayoutService,
    private interventionDataPrimaryService: InterventionDataPromiseLookupService,
    public userService: UserPromiseLookupService,
    private activeUserService: ActiveUserProfilePromiseService,
    private fwdRollPriceService: FwdRollPriceActionService,
    protected saveProfileNotificationService: BfxSaveProfileNotificationService,
    protected userProfileService: BfxUserProfileService,
    protected profileActionService: ProfilePromiseActionService,
    ccyPairService: CcyPairLookupService,
    ccyLookupService: CcyLookupService,
    protected notificationService: NotificationService,
    sysConfigService: SystemConfigPrimaryService,
  ) {
    this.initializeSubscriptions();

    this.activePortfolio = new FwdRollPricingModel(ccyPairService, ccyLookupService);
    // Subscribe for model changes that need to go to the server - subscripttion is lifelong
    this.activePortfolio.modelChangeCmd$.subscribe((update) =>
      this.processModelUpdate(this.activePortfolio, update),
    );

    this.activeUserService.getActiveUserData().then((activeUser: ActiveUser) => {
      this.activeUser = activeUser;
    });

    sysConfigService.isPrimed().then(() => {
      this.privatizePrivateComments =
        sysConfigService.hardGetSystemConfig(SysFeatures.privatisePrivateComments) == `Y`;
      this.privatizeClientComments =
        sysConfigService.hardGetSystemConfig(SysFeatures.privatiseClientComments) == `Y`;
    });
  }

  /**
   * Need to listen to platform  Unlock Intervention events
   */
  private initializeSubscriptions(): void {
    this.interventionDataPrimaryService.isPrimed().then(() => {
      this.interventionDataPrimaryService.getModelUpdateSubject().subscribe((dataItem) => {
        this.processUnlockUpdate(dataItem as CubiqTS.InterventionEventImpl);
      });
    });
  }

  /**
   * Handle the the Unlock of an active portfolio
   * @param event
   * @returns
   */
  private processUnlockUpdate(event: CubiqTS.InterventionEventImpl): void {
    logger.out.warn(`Fwd-Price-Roll: Subscription UPDATE `, event);
    if (
      event instanceof CubiqTS.InterventionEventImpl &&
      event.eventType === CubiqTS.InterventionEventType.PORTFOLIO_MANUAL_PRICING &&
      event.lockUserId == null
    ) {
      const portfolioId = event.fwdRollOrderData?.portfolioId?.toString();
      if (!portfolioId) {
        logger.out.warn("Cannot find portfolio Id in event", event);
        return;
      }
      if (!this.activePortfolio.isActive(portfolioId)) {
        return;
      }

      let userStr: string;
      if (CubiqTS.Long.isLong(event.unLockUserId)) {
        if (this.activeUser.userId === event.unLockUserId.toString()) {
          userStr = `you (${this.activeUser.username})`;
        } else {
          this.userService
            .getUsers([event.unLockUserId.toString()])
            .then((unlockUserDataset: Array<CubiqTS.UserImpl>) => {
              let userStr: string;
              if (Array.isArray(unlockUserDataset) && unlockUserDataset.length > 0) {
                userStr = unlockUserDataset[0].userName;
              } else {
                userStr = "another user";
              }
              this.activePortfolio.portfolioUnlocked(portfolioId, userStr);
            });
        }
      } else {
        userStr = "another user";
      }
      if (userStr) {
        this.activePortfolio.portfolioUnlocked(portfolioId, userStr);
      }
    }
  }

  /**********************************************************************/
  /* BLOTTER ACTIONS  */

  public fwdRollPrice(diEventId: string, portfolioId: string): Promise<void> {
    if (this.activePortfolio.isActive()) {
      return this.confirmReplace(this.activePortfolio, diEventId, portfolioId);
    } else {
      return this.lockForFwdPricing(diEventId, portfolioId);
    }
  }

  /**
   * Bring any active window to the front (if it exists) and then lock the specified portfolio
   * @param diEventId
   * @param portfolioId
   * @returns Promise that resolves when portfolio has been locked and view initialised
   */
  private lockForFwdPricing(diEventId: string, portfolioId: string) {
    // If window is open, bring it to the front
    const bringFwdPromise: Promise<boolean> = this.layoutService.restoreWindowAsPopout(
      this.activeWindowId,
    );

    return bringFwdPromise.then((restored) => {
      return this.fwdRollPriceService.lockForPricing(diEventId, portfolioId).then(
        (response) => {
          if (response instanceof CubiqTS.FwdRollPricingDataImpl) {
            this.privatizeResponse(diEventId, response);
            this.activePortfolio.initialise(response);
            if (!this.hasView) {
              this.initFwdRollPricingView();
            }
            if (!restored) {
              // Once a window is created the title is unchanged
              this.activeWindowId = this.activePortfolio.title;
            }
          }
        },
        (result: any) => {
          logger.out.error(`Error locking portfolio ${portfolioId}: Reason: ${result}`);
          const title: string = this.translate.instant("fwd_roll.error.title.lock");
          if (!(result instanceof CubiqTS.ErrorsImpl)) {
            result = new Error(result?.toString() ?? "<UNKNOWN>");
          }
          this.notificationService.handleGenericError(result, null, title);
        },
      );
    });
  }

  /**
   * The FWd roll response contains client, account and client comments info. We need to copy over the privatisation
   * done on the DI EVent order data to make sure we're not leaking private details.
   * @param eventId
   * @param response
   * @returns
   */
  private privatizeResponse(eventId: string, response: CubiqTS.FwdRollPricingDataImpl): void {
    const lookup: InterventionDataLookupService = this.interventionDataPrimaryService.getBase();
    const event = lookup.getInterventionData(eventId);

    let privatizedOrder = null;
    if (event?.fwdRollOrderData?.orders) {
      // check to see if any of the orders in the orginal event has been privatised
      for (const order of event.fwdRollOrderData.orders) {
        if (!privatizedOrder && StaticDataPrivatizationType.hasPrivatization(order)) {
          privatizedOrder = order;
        }
      }
    }

    if (privatizedOrder && response?.orderAllocations?.length) {
      // If any of the order sin the portfolio is privatised, then copy those privatised settings
      // into every allocation in the portfolio
      for (const alloc of response.orderAllocations) {
        alloc.clientCode = privatizedOrder.clientCode;
        alloc.accountName = privatizedOrder.accountName;
        if (this.privatizeClientComments) {
          // privatizedOrder's comment will not be privatized if feature is not set
          alloc.clientComments = privatizedOrder.comments1;
        }
        if (this.privatizePrivateComments) {
          alloc.privateComments = privatizedOrder.comments2;
        }
        for (const [key, value] of privatizedOrder.extendedInfo.entries()) {
          // Copy over extended data so we can always figure out what kind of privatisation is applied
          alloc.extendedInfo.set(key, value);
        }
      }
    }
  }

  public clear(portfolioIds: string[]): Promise<void> {
    if (Array.isArray(portfolioIds) !== true || portfolioIds.length == 0) {
      logger.out.info("Fwd Roll Clear rejected as missing protofolio ids");
      return Promise.resolve();
    }

    return new Promise<void>((res, rej) => {
      const message =
        portfolioIds.length === 1
          ? this.translate.instant("fwd_roll.confirm_clear.message", portfolioIds)
          : this.translate.instant("fwd_roll.confirm_clear.multi.message", [
              `${portfolioIds.length}`,
            ]);

      const dialogData = new BfxDialogData(
        BfxDialogCommand.DIALOG_GENERIC_CONFIRM_OPEN,
        null,
        null,
        {
          messages: [message],
          title: this.translate.instant("fwd_roll.confirm_clear.title"),
          callback: () => {
            this.fwdRollPriceService.clear(portfolioIds).then(
              () => res(),
              (...args) => rej(...args),
            );
          },
          callbackNegative: () => {
            logger.out.info("Fwd Roll Clear rejected by user");
            res();
          },
        },
      );

      this.sendDialogCommand(dialogData);
    });
  }

  public retry(portfolioIds: string[]): Promise<void> {
    if (Array.isArray(portfolioIds) !== true || portfolioIds.length == 0) {
      logger.out.info("Fwd Roll Retry rejected as missing protofolio ids");
      return Promise.resolve();
    }

    return new Promise<void>((res, rej) => {
      const message =
        portfolioIds.length === 1
          ? this.translate.instant("fwd_roll.confirm_retry.message", portfolioIds)
          : this.translate.instant("fwd_roll.confirm_retry.multi.message", [
              `${portfolioIds.length}`,
            ]);

      const dialogData = new BfxDialogData(
        BfxDialogCommand.DIALOG_GENERIC_CONFIRM_OPEN,
        null,
        null,
        {
          messages: [message],
          title: this.translate.instant("fwd_roll.confirm_retry.title"),
          callback: () => {
            this.fwdRollPriceService.retry(portfolioIds).then(
              () => res(),
              (...args) => rej(...args),
            );
          },
          callbackNegative: () => {
            logger.out.info("Fwd Roll Retry rejected by user");
            res();
          },
        },
      );

      this.sendDialogCommand(dialogData);
    });
  }

  /**
   * Returns a promise that resolves to true or false
   * @returns
   */
  public canUnlockForwardFirst(): Promise<boolean> {
    return new Promise<boolean>((res, _rej) => {
      const message = this.translate.instant("fwd_roll.unlock_forward_first.message");

      const dialogData = new BfxDialogData(
        BfxDialogCommand.DIALOG_GENERIC_CONFIRM_OPEN,
        null,
        null,
        {
          messages: [message],
          title: this.translate.instant("fwd_roll.unlock_forward_first.title"),
          callback: () => res(true),
          callbackNegative: () => {
            logger.out.info("Unlock forward first rejected by user");
            res(false);
          },
        },
      );
      this.sendDialogCommand(dialogData);
    });
  }

  /**********************************************************************/
  /* CORE */

  private initFwdRollPricingView(): void {
    // this.loadSettings().then(() => {
    let config: GLPopoutConfiguration = new GLPopoutConfiguration();
    config.initialState = GLPopoutState.POPOUT;
    config.undockBehaviour = GLPopoutUndockBehaviour.APPLY_BOUNDING_SIZE;
    config.popoutResizable = true;

    // if (this.savedSettings?.width != null && this.savedSettings?.height != null) {
    //   config.preferredSize = { width: this.savedSettings.width, height: this.savedSettings.height };
    //   // config.preferredSize = { width: 1000, height: 700 };
    // } else {
    config.preferredSize = { width: 1200, height: 700 };
    // }
    // if ( config.preferredSize.width < config.minimumSize.width){
    //   config.preferredSize.width = 1000;
    // }
    // if ( config.preferredSize.height < config.minimumSize.height){
    //   config.preferredSize.width = 700;
    // }
    config.minimumSize = { width: 800, height: 440 };

    // NB: AJB: Disable restoring of position until we can guarantee that it won't be drawn out of view
    // if ( this.savedSettings?.x != null && this.savedSettings?.y != null){
    //   config.position = { x: this.savedSettings.x, y: this.savedSettings.y };
    // }

    config.data = {};
    //config.data.selectedIds = event.selectedIds;
    config.data.payload = this.activePortfolio;
    config.data.title = this.translate.instant(textTemplates.title_key);

    this.layoutService.launchFromDashboard(
      config.data.title,
      DashboardItemType.FwdRollPricing,
      false,
      GoldenLayoutAdditionMode.DEFAULT,
      config,
    );
    // });
  }

  // private loadSettings(): Promise<void> {
  //   const profileKey = this.getProfileSettingKey();
  //   return this.profileActionService.getProfileConfig(profileKey).then((profileValue) => {
  //     try {
  //       this.savedSettings = JSON.parse(profileValue);
  //     } catch (e) {
  //       logger.out.warn("Failed to parse profile value for key ", profileKey, profileValue, e);
  //     }
  //   });
  // }

  public interfaceOnline(_portfolioModel: FwdRollPricingModel) {
    this.hasView = true;
  }

  public interfaceOffline(portfolioModel: FwdRollPricingModel) {
    portfolioModel.reset();
    this.hasView = false;
  }

  public getProfileSettingKey(): string {
    return `${ProfileSettingKeys.userProfileFwdRoll}}`;
  }

  public saveSettings(configSettings: any): void {
    let profileSettings: Array<CubiqTS.ProfileSettingImpl> = [];

    profileSettings.push(
      this.userProfileService.createProfileSettingObject(
        this.getProfileSettingKey(),
        JSON.stringify(configSettings),
      ),
    );
    this.profileActionService.updateProfile(profileSettings, false);
    this.savedSettings = configSettings;
  }

  // Dialog aupport
  /**
   * Launches a new dialog with a given dialog data
   * @param data the dialog data
   */
  public sendDialogCommand(data: BfxDialogData): void {
    this.blotterActionDialogSource.next(data);
  }

  public pricePortfolio(portfolio: FwdRollPricingModel) {
    const portfolioId = portfolio.portfolioId;
    if (!portfolio.validatePricing()) {
      this.showErrorDialog(
        "Please check entered prices - cannot sent to server while prices are not all specified",
      );
      return;
    }
    const dialogData = new BfxDialogData(BfxDialogCommand.DIALOG_GENERIC_OK_CANCEL, null, null, {
      messages: [this.translate.instant("fwd_roll.confirm_price.message")],
      title: this.translate.instant("fwd_roll.confirm_price.title"),
      callback: () => {
        if (portfolio.portfolioId == portfolioId) {
          this.submitPrice(portfolio);
        } else {
          logger.out.warn("Portfolio has changed since dialog confirmation was raised");
        }
      },
      callbackNegative: () => {
        logger.out.info("Price submission rejectd by user");
      },
    });
    this.sendDialogCommand(dialogData);
  }

  private showErrorDialog(message) {
    let payload = {
      title: "Forward Roll Error",
      messages: [message],
    };
    const dialogData = new BfxDialogData(BfxDialogCommand.DIALOG_GENERIC_OK, null, null, payload);
    this.sendDialogCommand(dialogData);
  }

  /**
   * Notify UI
   * @param portfolio
   */
  private submitPrice(portfolio: FwdRollPricingModel) {
    const portfolioId = portfolio.portfolioId;
    portfolio.interfaceCmdEventSource.next(
      new FwdRollPricingCmdPayload(FwdRollPricingCmd.DISABLE_FOR_PRICING),
    );
    const pricingRequest = portfolio.getPricingRequest();
    const pricingPromise = this.fwdRollPriceService.submitPricing(pricingRequest);
    pricingPromise
      .then((result) => {
        if (result instanceof CubiqTS.ErrorsImpl) {
          this.notificationService.handleGenericError(result, null, "FORWARD ROLL ERROR");
          portfolio.interfaceCmdEventSource.next(
            new FwdRollPricingCmdPayload(FwdRollPricingCmd.PRICING_FAIL, null),
          );
        } else {
          logger.out.info("Porfolio priced successfully");
          portfolio.completePricing(portfolioId);
        }
      })
      .catch((ex) => {
        this.showErrorDialog("Pricing submission failed: " + ex);
        portfolio.interfaceCmdEventSource.next(
          new FwdRollPricingCmdPayload(FwdRollPricingCmd.PRICING_FAIL, ex),
        );
      });
  }

  /**
   * Unlock all portfolios - this will not interact wih any open views - these willl have to depned on incoming Unlock notifications
   * to close
   * @param portfolio
   */
  public unlockPortfolios(portfolios: PortfolioPricingReference[]): Promise<boolean>[] {
    let retPromises = [];
    if (portfolios && portfolios.length > 0) {
      retPromises = portfolios.map((portfolioRef) =>
        this.fwdRollPriceService
          .unlock(portfolioRef.portfolioId)
          .catch((error) => logger.out.warn(error)),
      );
    }

    return retPromises;
  }

  /**
   * Unlock the portfolio associated with the specified portfolio. This will initiate the flow by create the unlock request
   * and sending it to the server, processing the response when it arrives.
   * @param portfolio
   */
  public unlock(portfolio: FwdRollPricingModel): Promise<boolean> {
    if (portfolio.isComplete) {
      return Promise.resolve(true);
    }

    const portfolioId = portfolio.portfolioId;
    const unlockPromise = this.fwdRollPriceService.unlock(portfolioId);
    const retPromise = unlockPromise.then(
      () => {
        portfolio.unlockSuccess(portfolioId);
        return true;
      },
      (result: any) => {
        logger.out.error(`Error unlocking portfolio ${portfolio.portfolioId}: Reason: ${result}`);
        const title: string = this.translate.instant("fwd_roll.error.title.unlock");
        if (!(result instanceof CubiqTS.ErrorsImpl)) {
          result = new Error(result?.toString() ?? "<UNKNOWN>");
        }

        this.notificationService.handleGenericError(result, null, title);
        portfolio.unlockFail(portfolioId, result);
        return false;
      },
    );
    return retPromise;
  }

  /**
   * Save the portfolio pricing data. This will initiate the flow by create the save request
   * and sending it to the server, processing the response when it arrives.
   * @param portfolio
   */
  public save(portfolio: FwdRollPricingModel): Promise<void> {
    const portfolioId = portfolio.portfolioId;
    const saveRequest = portfolio.getSaveRequest();
    const savePromise = this.fwdRollPriceService.savePricing(saveRequest);
    savePromise
      .then((result) => {
        if (result instanceof CubiqTS.ErrorsImpl) {
          this.notificationService.handleGenericError(result, null, "FORWARD ROLL SAVE ERROR");
          portfolio.interfaceCmdEventSource.next(
            new FwdRollPricingCmdPayload(FwdRollPricingCmd.PRICING_SAVE_FAIL, null),
          );
        } else {
          logger.out.info("Porfolio price saved successfully");
          portfolio.updateSaveDetails(
            portfolioId,
            CubiqTS.Long.fromString(this.activeUser.userId),
            CubiqTS.moment(),
          );
        }
      })
      .catch((ex) => {
        this.showErrorDialog("Portfolio price save failed: " + ex);
      });
    return savePromise;
  }

  public refresh(portfolio: FwdRollPricingModel): void {
    portfolio.refresh();
  }

  private confirmReplace(
    portfolio: FwdRollPricingModel,
    newDiEventId: string,
    newPortfolioId,
  ): Promise<void> {
    const retPromise: Promise<void> = new Promise((res) => {
      this.layoutService.restoreWindowAsPopout(this.activeWindowId).then((_restored) => {
        let dialogData: BfxDialogData;
        // Close any existing replace dialog
        dialogData = new BfxDialogData(
          BfxDialogCommand.DIALOG_FWD_ROLL_SAVE_ABORT_CLOSE,
          CONFIRM_DIALOG_ID,
          null,
          null,
        );
        this.sendDialogCommand(dialogData);

        dialogData = new BfxDialogData(
          BfxDialogCommand.DIALOG_FWD_ROLL_SAVE_ABORT_OPEN,
          CONFIRM_DIALOG_ID,
          null,
          {
            currentPortfolioId: portfolio.portfolioId,
            newPortfolioId,
            isExit: false,
            callbacks: {
              saveOperation: () => {
                logger.out.info("Save and Open");
                this.save(portfolio).then(() => {
                  // Fire and forget about the unlock
                  this.unlock(portfolio);
                  this.lockForFwdPricing(newDiEventId, newPortfolioId);
                  res();
                });
              },
              abortOperation: () => {
                logger.out.info("Abort and Open");
                // Fire and forget about the unlock
                this.unlock(portfolio);
                this.lockForFwdPricing(newDiEventId, newPortfolioId);
                res();
              },
              cancel: () => {
                logger.out.info("Cancel and No Open - ignoring request");
                res();
              },
            },
          },
          // portfolio.getDocument(),
        );
        this.sendDialogCommand(dialogData);
      });
    });
    return retPromise;
  }

  public confirmClose(portfolio: FwdRollPricingModel): Promise<boolean> {
    const retPromise: Promise<boolean> = new Promise((res) => {
      if (!portfolio.hasChanges()) {
        res(true);
        return;
      }
      let dialogData: BfxDialogData;

      dialogData = new BfxDialogData(
        BfxDialogCommand.DIALOG_FWD_ROLL_SAVE_ABORT_OPEN,
        CONFIRM_DIALOG_ID,
        null,
        {
          currentPortfolioId: portfolio.portfolioId,
          isExit: true,
          callbacks: {
            saveOperation: () => {
              logger.out.info("Save and Close");
              this.save(portfolio).then(() => {
                res(true);
              });
            },
            abortOperation: () => {
              logger.out.info("Abort and Close");
              res(true);
            },
            cancel: () => {
              logger.out.info("Cancel and No Close - ignoring request");
              res(false);
            },
          },
        },
        // portfolio.getDocument(),
      );
      this.sendDialogCommand(dialogData);
    });
    return retPromise;
  }

  /**
   * Send out a model update to the server and process the response
   * @param portfolio
   * @param request
   */
  private processModelUpdate(
    portfolio: FwdRollPricingModel,
    request: CubiqTS.FwdRollChangeRequest,
  ): void {
    const portfolioId = portfolio.portfolioId;
    const updatePromise = this.fwdRollPriceService.updatePriceRequest(request);
    updatePromise.then(
      (response: CubiqTS.FwdRollChangeResponse) => {
        portfolio.applyModelChanges(portfolioId, response.updates);
      },
      (reason) => {
        logger.out.error("update failed: reason" + reason);
        if (portfolio.errorResetPending) {
          // We have failed to get a copy of the portfolio after an error
          // so we will not retry - prevents an infinite loop
          logger.out.error("Aborting pricing as cannot sync portfolio model");
        } else {
          portfolio.processUpdateError(portfolioId, reason);
        }
      },
    );
  }
}
